import type { SifaFrist } from './betreuungTypes';

/**
 * Fristenregister (Faelligkeiten & Vorsorge) — REINE Logik, kein Supabase,
 * keine Uhr: `heute` kommt immer vom Aufrufer. So laesst sich jede Ampel und
 * jeder Turnus gegen ein festes Datum testen.
 *
 * Datumsangaben sind ISO-Zeichenketten (yyyy-mm-dd) und werden per
 * Zeichenkette zerlegt — KEIN `new Date('yyyy-mm-dd')`: das ist in Node UTC
 * und im Browser je nach Zone einen Tag zu frueh (Lehre zertifikatPdf.ts).
 * Gerechnet wird ueber `Date.UTC`, damit Sommerzeit-Umstellungen keine
 * 23-Stunden-Tage erzeugen.
 */

/** Was der Aufrufer beim Anlegen einer Frist setzen darf (Bauplan §2). */
export type NeueFrist = Pick<SifaFrist, 'titel' | 'kategorie' | 'faelligAm'> &
  Partial<Pick<SifaFrist, 'mitarbeiterId' | 'intervallMonate' | 'zustaendig' | 'notiz'>>;

export type FristAmpel = 'rot' | 'gelb' | 'gruen' | 'erledigt';

/** Ab so vielen Tagen vor der Faelligkeit wird die Ampel gelb (Bauplan §1: „gelb < 60 Tage"). */
export const AMPEL_GELB_TAGE = 60;

/** Ab so vielen Beschaeftigten ist ein Arbeitsschutzausschuss Pflicht (§ 11 ASiG). */
export const ASA_PFLICHT_AB_BESCHAEFTIGTEN = 20;

// ─── Datum ──────────────────────────────────────────────────────────────────

interface Kalendertag {
  jahr: number;
  monat: number; // 1–12
  tag: number;   // 1–31
}

/** „2026-09-09" bzw. „2026-09-09T13:05:00Z" → { 2026, 9, 9 }; sonst null. */
export const parseIsoDatum = (iso: string | null | undefined): Kalendertag | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec((iso || '').trim());
  if (!m) return null;
  const jahr = Number(m[1]);
  const monat = Number(m[2]);
  const tag = Number(m[3]);
  if (monat < 1 || monat > 12 || tag < 1 || tag > tageImMonat(jahr, monat)) return null;
  return { jahr, monat, tag };
};

const zweistellig = (n: number): string => (n < 10 ? `0${n}` : String(n));

const kalendertagZuIso = (t: Kalendertag): string => `${t.jahr}-${zweistellig(t.monat)}-${zweistellig(t.tag)}`;

/** Lokales Datum eines Date-Objekts als yyyy-mm-dd — NICHT toISOString (das ist UTC). */
export const isoDatum = (d: Date): string =>
  kalendertagZuIso({ jahr: d.getFullYear(), monat: d.getMonth() + 1, tag: d.getDate() });

/** `heute` als ISO-Datum, egal ob Date oder schon Zeichenkette. */
const heuteIso = (heute: Date | string): string => (typeof heute === 'string' ? heute.slice(0, 10) : isoDatum(heute));

/** Anzahl Tage im Monat (Schaltjahr beruecksichtigt). */
export const tageImMonat = (jahr: number, monat: number): number => new Date(Date.UTC(jahr, monat, 0)).getUTCDate();

const utcTage = (t: Kalendertag): number => Math.round(Date.UTC(t.jahr, t.monat - 1, t.tag) / 86_400_000);

/** Ganze Tage von `von` bis `bis` (positiv, wenn `bis` spaeter liegt); null bei unlesbarem Datum. */
export const tageDifferenz = (vonIso: string, bisIso: string): number | null => {
  const von = parseIsoDatum(vonIso);
  const bis = parseIsoDatum(bisIso);
  if (!von || !bis) return null;
  return utcTage(bis) - utcTage(von);
};

/** ISO-Datum plus n Tage (n darf negativ sein). Unlesbares Datum bleibt, wie es ist. */
export const plusTage = (iso: string, tage: number): string => {
  const t = parseIsoDatum(iso);
  if (!t) return iso;
  const d = new Date(Date.UTC(t.jahr, t.monat - 1, t.tag + tage));
  return kalendertagZuIso({ jahr: d.getUTCFullYear(), monat: d.getUTCMonth() + 1, tag: d.getUTCDate() });
};

/**
 * Faelligkeit der NAECHSTEN Zeile: `iso` + `monate`, Tag auf das Monatsende
 * geklemmt (31.01. + 1 Monat = 28./29.02., 31.03. + 1 = 30.04.).
 *
 * Gerechnet wird vom URSPRUENGLICHEN Faelligkeitsdatum, nicht vom
 * Erledigungstag (sifaBetreuungService.erledigeFrist uebergibt `faelligAm`):
 * wer die Vorsorge vom 15.03. am 02.04. nachholt, ist trotzdem am 15.03. des
 * Folgejahres wieder dran — sonst wanderte jeder Turnus mit jeder Verspaetung
 * nach hinten und nach zehn Jahren laege die Jahresunterweisung im Herbst.
 *
 * Wirft bei unlesbarem Datum oder Intervall ausserhalb 1–120 — beides prueft
 * der Service vorher; hier waere ein stilles Weiterrechnen ein falsches Datum.
 */
export const naechsteFaelligkeit = (iso: string, monate: number): string => {
  const t = parseIsoDatum(iso);
  if (!t) throw new Error(`Unlesbares Fälligkeitsdatum: „${iso}"`);
  if (!Number.isInteger(monate) || monate < 1 || monate > 120) {
    throw new Error(`Wiederholung in Monaten muss zwischen 1 und 120 liegen (war ${monate}).`);
  }
  const gesamt = t.monat - 1 + monate; // 0-basiert
  const jahr = t.jahr + Math.floor(gesamt / 12);
  const monat = (gesamt % 12) + 1;
  const tag = Math.min(t.tag, tageImMonat(jahr, monat));
  return kalendertagZuIso({ jahr, monat, tag });
};

// ─── Ampel ──────────────────────────────────────────────────────────────────

/**
 * rot = ueberfaellig (faellig < heute), gelb = faellig innerhalb der naechsten
 * 60 Tage (heute eingeschlossen), gruen = spaeter, erledigt = `erledigtAm`
 * gesetzt (Historie, keine Farbe). Unlesbares Datum → rot: eine Frist, deren
 * Datum niemand lesen kann, braucht Aufmerksamkeit, keine Beruhigung.
 */
export const fristAmpel = (
  frist: Pick<SifaFrist, 'faelligAm' | 'erledigtAm'>,
  heute: Date | string,
): FristAmpel => {
  if (frist.erledigtAm) return 'erledigt';
  const tage = tageDifferenz(heuteIso(heute), frist.faelligAm);
  if (tage === null || tage < 0) return 'rot';
  if (tage <= AMPEL_GELB_TAGE) return 'gelb';
  return 'gruen';
};

// ─── Sortierung ─────────────────────────────────────────────────────────────

/**
 * Offene zuerst, nach Faelligkeit aufsteigend (die dringendste oben), dann
 * Titel; erledigte danach, juengst erledigte zuerst. Liefert eine neue Liste.
 */
export const sortiereFristen = <T extends Pick<SifaFrist, 'faelligAm' | 'erledigtAm' | 'titel'>>(fristen: readonly T[]): T[] =>
  [...fristen].sort((a, b) => {
    const aOffen = !a.erledigtAm;
    const bOffen = !b.erledigtAm;
    if (aOffen !== bOffen) return aOffen ? -1 : 1;
    if (aOffen) {
      return a.faelligAm.localeCompare(b.faelligAm) || a.titel.localeCompare(b.titel, 'de');
    }
    return (
      (b.erledigtAm ?? '').localeCompare(a.erledigtAm ?? '') ||
      b.faelligAm.localeCompare(a.faelligAm) ||
      a.titel.localeCompare(b.titel, 'de')
    );
  });

// ─── ASA-Vorschlag ──────────────────────────────────────────────────────────

/** Letzter Tag des Quartals, in dem `iso` liegt. */
const quartalsende = (t: Kalendertag): Kalendertag => {
  const monat = Math.ceil(t.monat / 3) * 3;
  return { jahr: t.jahr, monat, tag: tageImMonat(t.jahr, monat) };
};

/**
 * Vorschlag „ASA-Sitzung" beim ersten Oeffnen des Registers: ab mehr als 20
 * Beschaeftigten ist der Arbeitsschutzausschuss Pflicht und tagt mindestens
 * vierteljaehrlich (§ 11 ASiG). Faellig zum naechsten Quartalsende (heute
 * eingeschlossen — wer am 30.09. oeffnet, bekommt den 30.09.), Wiederholung
 * 3 Monate. null bei 20 oder weniger, unbekannt oder unsinnig.
 */
export const asaVorschlag = (beschaeftigte: number | null | undefined, heute: Date | string): NeueFrist | null => {
  if (typeof beschaeftigte !== 'number' || !Number.isFinite(beschaeftigte)) return null;
  if (beschaeftigte <= ASA_PFLICHT_AB_BESCHAEFTIGTEN) return null;
  const t = parseIsoDatum(heuteIso(heute));
  if (!t) return null;
  return {
    titel: 'ASA-Sitzung',
    kategorie: 'asa',
    faelligAm: kalendertagZuIso(quartalsende(t)),
    intervallMonate: 3,
    notiz: `Arbeitsschutzausschuss nach § 11 ASiG — Pflicht ab mehr als ${ASA_PFLICHT_AB_BESCHAEFTIGTEN} Beschäftigten, mindestens vierteljährlich.`,
  };
};

// ─── Zusammenfassung ────────────────────────────────────────────────────────

export interface FristenZusammenfassung {
  offen: number;
  ueberfaellig: number;
  /** Faellig innerhalb der naechsten 60 Tage (heute eingeschlossen), nicht ueberfaellig. */
  bald: number;
  gruen: number;
  erledigt: number;
  /** Die offene Frist mit dem fruehesten Datum, null wenn keine offen ist. */
  naechste: { titel: string; faelligAm: string } | null;
}

/**
 * Kopfzahlen fuer die Liste — dieselbe Regel wie `fristAmpel`, damit die
 * Zahl ueber der Liste zu den Farben in der Liste passt. Die Kennzahlen fuer
 * Kachel und Hub kommen NICHT hierher, sondern aus head-Counts
 * (sifaBetreuungService.ladeKennzahlen) — dort wird keine Liste geladen.
 */
export const fristenZusammenfassung = (
  fristen: readonly Pick<SifaFrist, 'titel' | 'faelligAm' | 'erledigtAm'>[],
  heute: Date | string,
): FristenZusammenfassung => {
  const z: FristenZusammenfassung = { offen: 0, ueberfaellig: 0, bald: 0, gruen: 0, erledigt: 0, naechste: null };
  for (const f of fristen) {
    const ampel = fristAmpel(f, heute);
    if (ampel === 'erledigt') {
      z.erledigt += 1;
      continue;
    }
    z.offen += 1;
    if (ampel === 'rot') z.ueberfaellig += 1;
    else if (ampel === 'gelb') z.bald += 1;
    else z.gruen += 1;
    if (!z.naechste || f.faelligAm < z.naechste.faelligAm) z.naechste = { titel: f.titel, faelligAm: f.faelligAm };
  }
  return z;
};
